import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Table,
  FormGroup,
  Label,
} from "reactstrap";

import { withTranslation } from "react-i18next";

import {
  selectStyle,
  getOptions,
  getPages,
  setClassName,
  renderSpinner,
  renderClickSearch,
  renderAction,
  renderDeleteModal,
  renderPagination,
  renderNotFound,
} from "../../Libs/CrudUtils/crudUtils";

import { showToast } from "../../Libs/CustomMethods/showToast";

import Breadcrumbs from "../../components/Common/Breadcrumb";

import { useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";

import { schemaIndex } from "./SurveyValidation.js";

import {
  getDataIndex,
  dataDelete,
  apiReset,
} from "../../store/surveys/actions";

import Select from "react-select";

import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";

const Surveys = ({ t }) => {
  const dispatch = useDispatch();
  const { register, handleSubmit, setValue, errors } = useForm({
    resolver: yupResolver(schemaIndex),
  });

  const qtdDefault = 7;

  const {
    dataIndex,
    dataForm,
    loadingDataIndex,
    loadingDataDelete,
    error,
    success,
  } = useSelector((state) => state.Survey);

  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [pagesArray, setPagesArray] = useState([]);
  const [dataFilter, setDataFilter] = useState({});
  const [dataSelected, setDataSelected] = useState({});

  // #BEGIN - Editable variables

  const mainPath = "/dashboard";
  const mainTitle = t("Dashboard");
  const path = "/surveys";
  const pathTitle = t("Surveys");
  const showPath = "/survey";
  const showTitle = t("Survey");

  const renderFilter = () => (
    <form onSubmit={handleSubmit(handleFilter)}>
      <Row>
        <Col xs="12" md="3" lg="3" xl="3" className="mb-3">
          <FormGroup>
            <Label>{t("Name")}</Label>
            <input
              ref={() => register({ name: "name" })}
              type="text"
              className={setClassName(errors.name, "form-control")}
              placeholder={t("Enter name")}
              onChange={(e) => {
                let value = e?.target?.value;
                updateValue("name", value);
              }}
              value={dataFilter?.name ?? ""}
            />
            <div className="invalid-feedback d-block">
              {t(errors.name?.message)}
            </div>
          </FormGroup>
        </Col>
        <Col xs="12" md="3" lg="3" xl="3" className="mb-3">
          <FormGroup className="select2-container">
            <Label>{t("Type")}</Label>
            <Select
              ref={() => register({ name: "type" })}
              classNamePrefix="select2-selection"
              className={setClassName(errors.type, " select2-selection")}
              styles={selectStyle}
              placeholder={t("Choose") + "..."}
              options={getOptions(
                [
                  { id: 0, name: "ALL TYPES" },
                  ...(dataForm?.types?.map((e) => ({ id: e, name: e })) ?? []),
                ],
                loadingDataIndex
              )}
              onChange={(e) => {
                let value = e?.value;
                updateValue("type", value);
              }}
              value={
                getOptions(
                  [
                    { id: 0, name: "ALL TYPES" },
                    ...(dataForm?.types?.map((e) => ({ id: e, name: e })) ?? []),
                  ],
                  loadingDataIndex
                )?.find((e) => e.value === dataFilter?.type) ?? null
              }
            />

            <div className="invalid-feedback d-block">
              {t(errors.type?.message)}
            </div>
          </FormGroup>
        </Col>
        <Col
          xs="12"
          md="3"
          lg="auto"
          className="mt-auto mb-3 filter-create-btn"
        >
          <Button
            type="submit"
            color="primary"
            className="waves-effect waves-light w-100 mb-3 mr-3"
          >
            <i className="mdi mdi-feature-search-outline mr-1"></i>
            {t("Search")}
          </Button>
        </Col>
        <Col
          xs="12"
          md="3"
          lg="auto"
          className="mt-auto mb-3 ml-auto filter-create-btn"
        >
          <div className="text-sm-right mb-3">
            <Link to={showPath}>
              <Button
                type="button"
                color="success"
                className={"waves-effect waves-light w-100 mr-2 "}
              >
                <i className="mdi mdi-plus mr-1"></i>
                {`${t("Add")} ${showTitle}`}
              </Button>
            </Link>
          </div>
        </Col>
      </Row>
    </form>
  );

  const renderList = () => (
    <div className="table-responsive">
      <PerfectScrollbar>
        <Table className="table table-centered table-nowrap">
          <thead className="thead-light">
            <tr>
              <th>{t("Name")}</th>
              <th>{t("Type")}</th>
              <th className="text-right pr-3">{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {loadingDataIndex
              ? renderSpinner()
              : notFound
              ? renderClickSearch(t)
              : dataIndex?.data?.length > 0
              ? dataIndex.data.map((item, key) => (
                  <tr key={key}>
                    <td className="column-text-overflow-25">
                      {item?.name ?? "-"}
                    </td>
                    <td className="column-text-overflow-25">
                      {t(item?.type) ?? "-"}
                    </td>
                    <td className="d-flex justify-content-end font-size-18">
                      {renderAction(
                        item,
                        key,
                        showPath,
                        toggleModal,
                        setDataSelected,
                        t
                      )}
                    </td>
                  </tr>
                ))
              : renderNotFound(t)}
          </tbody>
        </Table>
      </PerfectScrollbar>
    </div>
  );

  // #END - Editable variables

  const toggleModal = () => {
    setModal(!modal);
  };

  const handlePage = useCallback(
    (value) => {
      setPage(value);
      dispatch(getDataIndex(value, qtdDefault, dataFilter, true));
    },
    [dataFilter, dispatch]
  );

  const handlePrevPage = () => {
    if (page > 1) {
      handlePage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < pagesArray?.length) {
      handlePage(page + 1);
    }
  };

  const handleFilter = useCallback(() => {
    handlePage(1);
  }, [handlePage]);

  const handleDelete = () => {
    dispatch(dataDelete(dataSelected?.id));
  };

  const updateValue = useCallback(
    (target, value) => {
      setValue(target, value, {
        shouldValidate: true,
      });
      setDataFilter((prev) => ({
        ...prev,
        [target]: value,
      }));
    },
    [setValue]
  );

  const onToastHidden = useCallback(() => {
    dispatch(apiReset());
    if (success) {
      handleFilter();
    }
  }, [dispatch, handleFilter, success]);

  useEffect(() => {
    let total = dataIndex?.total;
    let arrayFill = getPages(total, qtdDefault);
    setPagesArray(arrayFill);
    setNotFound(!dataIndex?.data);
  }, [dataIndex?.data, dataIndex?.total]);

  useEffect(() => {
    !dataForm && dispatch(getDataIndex(null, null, null, !dataForm));
  }, [dataForm, dispatch]);

  useEffect(() => {
    success && showToast(t(success), "success", onToastHidden);
  }, [success, onToastHidden, t]);
  useEffect(() => {
    error && showToast(t(error), "error", onToastHidden);
  }, [error, onToastHidden, t]);

  useEffect(() => {
    handleFilter();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            mainPath={mainPath}
            path={path}
            title={mainTitle}
            breadcrumbItem={pathTitle}
          />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody className="card-height">
                  {renderFilter()}
                  {renderList()}
                  {renderPagination(
                    page,
                    pagesArray,
                    handlePrevPage,
                    handlePage,
                    handleNextPage
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {renderDeleteModal(
        modal,
        dataSelected?.name,
        loadingDataDelete,
        toggleModal,
        handleDelete,
        t
      )}
    </React.Fragment>
  );
};

export default withTranslation()(Surveys);
